/**
 * Utility functions for reading CSS custom properties (CSS variables)
 * This provides a single source of truth for colors defined in global.css
 */

/**
 * Reads CSS custom properties from the document root
 * Returns the actual color values defined in CSS variables
 */
export const getStatusColors = () => {
  const root = document.documentElement;
  const getProperty = (property: string) =>
    getComputedStyle(root).getPropertyValue(property).trim();

  return {
    COMPLETED: getProperty('--status-completed'),
    IN_PROGRESS: getProperty('--status-in-progress'),
    NOT_STARTED: getProperty('--status-not-started'),
    ASSIGNED: getProperty('--status-assigned'),
    EXPIRED: getProperty('--status-expired'),
    OVERDUE: getProperty('--status-overdue'),
    CANCELLED: getProperty('--status-cancelled'),
  };
};

/**
 * Status colors object that reads from CSS custom properties
 * This ensures consistency between CSS classes and JavaScript usage
 */
export const STATUS_COLORS = {
  get COMPLETED() {
    return getStatusColors().COMPLETED;
  },
  get IN_PROGRESS() {
    return getStatusColors().IN_PROGRESS;
  },
  get NOT_STARTED() {
    return getStatusColors().NOT_STARTED;
  },
  get ASSIGNED() {
    return getStatusColors().ASSIGNED;
  },
  get EXPIRED() {
    return getStatusColors().EXPIRED;
  },
  get OVERDUE() {
    return getStatusColors().OVERDUE;
  },
  get CANCELLED() {
    return getStatusColors().CANCELLED;
  },

  // Legacy compatibility with existing STATUS_COLORS usage
  get SUCCESS() {
    return getStatusColors().COMPLETED;
  },
  get INFO() {
    return getStatusColors().IN_PROGRESS;
  },
  get WARNING() {
    return getStatusColors().NOT_STARTED;
  },
  get ERROR() {
    return getStatusColors().EXPIRED;
  },
  get SECONDARY() {
    return getStatusColors().CANCELLED;
  },
} as const;

export type StatusColor = string;

export const getStatusBgColors = () => {
  const root = document.documentElement;
  const getProperty = (property: string) =>
    getComputedStyle(root).getPropertyValue(property).trim();

  return {
    COMPLETED: getProperty('--status-completed-bg'),
    IN_PROGRESS: getProperty('--status-in-progress-bg'),
    NOT_STARTED: getProperty('--status-not-started-bg'),
    ASSIGNED: getProperty('--status-assigned-bg'),
    EXPIRED: getProperty('--status-expired-bg'),
    OVERDUE: getProperty('--status-overdue-bg'),
    CANCELLED: getProperty('--status-cancelled-bg'),
  };
};

export const STATUS_BG_COLORS = {
  get COMPLETED() {
    return getStatusBgColors().COMPLETED;
  },
  get IN_PROGRESS() {
    return getStatusBgColors().IN_PROGRESS;
  },
  get NOT_STARTED() {
    return getStatusBgColors().NOT_STARTED;
  },
  get ASSIGNED() {
    return getStatusBgColors().ASSIGNED;
  },
  get EXPIRED() {
    return getStatusBgColors().EXPIRED;
  },
  get OVERDUE() {
    return getStatusBgColors().OVERDUE;
  },
  get CANCELLED() {
    return getStatusBgColors().CANCELLED;
  },

  // Legacy compatibility with existing STATUS_COLORS usage
  get SUCCESS() {
    return getStatusBgColors().COMPLETED;
  },
  get INFO() {
    return getStatusBgColors().IN_PROGRESS;
  },
  get WARNING() {
    return getStatusBgColors().NOT_STARTED;
  },
  get ERROR() {
    return getStatusBgColors().EXPIRED;
  },
  get SECONDARY() {
    return getStatusBgColors().CANCELLED;
  },
} as const;
